import { Icon, Page, InlineStack, Text, Box, BlockStack, Card, Tabs, Link, Button, Divider, ButtonGroup, Badge } from '@shopify/polaris';
import { BillFilledIcon, CashDollarIcon, EditIcon, InfoIcon, StopCircleIcon, SunIcon, TaxFilledIcon } from '@shopify/polaris-icons';
import helpers from 'helpers/index';
import { ENUM_VEHICLE_STATUS, useGetVehicle } from 'queries/vehicle.query';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ListLog from './list_log';
import IncomeLog from './income_log';
import { Helmet } from 'react-helmet-async';
import VehicleModal from './vehicle.modal';
import dateandtime from 'date-and-time';
import ListLogDetail from './list_log.view';

export default function VehicleView() {
  let { vehicle_id, mode = 'log' } = useParams();

  /** Mode có thể là log: truy vấn vào vehicle_log và mode là income: truy vấn vào vehicle_income, hiện tại vehicle_income chưa có */

  const { data: vehicleData, isLoading: loadingVehicleInformation } = useGetVehicle(vehicle_id);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 800px)');
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      const matches = 'matches' in event ? event.matches : mediaQuery.matches;
      setIsMobile(matches);
    };

    handleChange(mediaQuery);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange as (event: MediaQueryListEvent) => void);
    } else {
      mediaQuery.addListener(handleChange as (event: MediaQueryListEvent) => void);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange as (event: MediaQueryListEvent) => void);
      } else {
        mediaQuery.removeListener(handleChange as (event: MediaQueryListEvent) => void);
      }
    };
  }, []);

  const [newModelactive, setNewModelactive] = useState(false);
  const toggleNewActive = useCallback(() => setNewModelactive((active) => !active), []);

  return (
    <>
      <Helmet>
        <title>Nhật trình và thu nhập của xe</title>
      </Helmet>
      <Page
        title={vehicleData?.name + ` (${vehicleData?.license_plate})`}
        titleMetadata={
          vehicleData?.vehicle_status === ENUM_VEHICLE_STATUS.ACTIVE ? (
            <Badge icon={SunIcon} tone="success-strong">
              Đang hoạt động
            </Badge>
          ) : (
            <Badge tone="critical" icon={StopCircleIcon}>
              Ngừng hoạt động
            </Badge>
          )
        }
        subtitle={`Hoạt động từ ${dateandtime.format(new Date(Number(vehicleData?.createdAt)), 'HH:mm DD/MM/YYYY')} tới ${vehicleData?.endedAt ? dateandtime.format(new Date(Number(vehicleData?.endedAt)), 'HH:mm DD/MM/YYYY') : 'hiện tại'}`}
        backAction={{
          content: 'Quay lại danh sách',
          url: '/vehicle',
        }}
        fullWidth
        primaryAction={{
          onAction: toggleNewActive,
          content: 'Sửa thông tin',
          icon: EditIcon,
        }}
      >
        <InlineStack gap={isMobile ? '200' : '400'} wrap>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <Box as="span">
                    <Icon source={InfoIcon} tone="subdued" />
                  </Box>
                  <Text as="span" tone="subdued">
                    Tổng số chuyến
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(vehicleData?.sum?.sum_of_log || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <Box as="span">
                    <Icon source={InfoIcon} tone="subdued" />
                  </Box>
                  <Text as="span" tone="subdued">
                    Tổng số KM
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(vehicleData?.sum?.sum_of_distance || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>

          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <Box as="span">
                    <Icon source={CashDollarIcon} tone="subdued" />
                  </Box>
                  <Text as="span" tone="subdued">
                    Thu nhập
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(vehicleData?.sum?.sum_of_income || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <Box as="span">
                    <Icon source={CashDollarIcon} tone="subdued" />
                  </Box>
                  <Text as="span" tone="subdued">
                    Thời gian vận hành
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.subtractTimeHistoryDurationDays(vehicleData?.sum?.sum_of_time)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
        </InlineStack>

        <br />

        <InlineStack align="start" blockAlign="center" gap="200">
          <ButtonGroup>
            <Button variant="plain" tone="critical" url={`/vehicle/${vehicle_id}/log`}>
              Nhật trình
            </Button>
            <Button variant="plain" tone="success" url={`/vehicle/${vehicle_id}/income`}>
              Hóa đơn &amp; doanh thu
            </Button>
          </ButtonGroup>
        </InlineStack>

        <br />

        {mode === 'log' && <ListLog vehicle_id={vehicle_id} />}
        {mode === 'income' && <IncomeLog vehicle_id={vehicle_id} />}
        {mode === 'detail' && <ListLogDetail />}
      </Page>

      {
        <VehicleModal
          show={newModelactive}
          currentVehicleID={BigInt(vehicle_id)}
          onClose={() => {
            setNewModelactive(false);
          }}
        />
      }
    </>
  );
}

